import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * Notification / UI sound playback.
 * Reuses one unlocked HTMLAudioElement per key (new Audio() each time breaks autoplay).
 * If play is blocked (background tab / no gesture), queues and retries on focus/click.
 */
@Injectable({ providedIn: 'root' })
export class AudioService {
  private unlocked = false;
  private readonly srcMap = new Map<string, string>();
  private readonly audioMap = new Map<string, HTMLAudioElement>();
  private readonly isBrowser: boolean;
  /** Clip waiting to play after autoplay was blocked. */
  private pendingKey: string | null = null;
  private listenersBound = false;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.bindUnlockListeners();
    }
  }

  private resolveSrc(src: string): string {
    try {
      return new URL(src, document.baseURI).href;
    } catch {
      return src;
    }
  }

  private bindUnlockListeners(): void {
    if (this.listenersBound) return;
    this.listenersBound = true;

    const onGesture = () => {
      void this.unlockAndFlushPending();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void this.unlockAndFlushPending();
      }
    };

    document.addEventListener('click', onGesture, { capture: true });
    document.addEventListener('keydown', onGesture, { capture: true });
    document.addEventListener('touchstart', onGesture, { capture: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  }

  /** Silent unlock + play any sound that was blocked while the tab was hidden. */
  private async unlockAndFlushPending(): Promise<void> {
    if (this.srcMap.size === 0) return;
    // Already unlocked and nothing queued — do not play on every click.
    if (this.unlocked && !this.pendingKey) return;

    const key = this.pendingKey ?? Array.from(this.srcMap.keys())[0];
    const audio = this.ensureAudio(key);
    if (!audio) return;

    const wasPending = this.pendingKey !== null;
    try {
      if (!wasPending) {
        audio.volume = 0;
      } else {
        audio.volume = 1;
        audio.currentTime = 0;
      }

      await audio.play();

      if (!wasPending) {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1;
      }

      this.unlocked = true;
      this.pendingKey = null;
    } catch {
      // Still blocked — keep listeners for a later gesture.
    }
  }

  private ensureAudio(key: string): HTMLAudioElement | null {
    const src = this.srcMap.get(key);
    if (!src) return null;

    let audio = this.audioMap.get(key);
    if (!audio) {
      audio = new Audio(this.resolveSrc(src));
      audio.preload = 'auto';
      this.audioMap.set(key, audio);
    }
    return audio;
  }

  /** Register a clip source once. */
  register(key: string, src: string): void {
    if (!this.isBrowser || this.srcMap.has(key)) return;
    this.srcMap.set(key, src);
    this.ensureAudio(key);
  }

  /**
   * Play a registered clip on the same unlocked element.
   * On autoplay block, queues playback for the next user gesture / tab focus.
   */
  play(key: string): void {
    if (!this.isBrowser) return;

    const audio = this.ensureAudio(key);
    if (!audio) return;

    audio.volume = 1;
    try {
      audio.currentTime = 0;
    } catch {
      // Ignore seek errors while loading.
    }

    void audio
      .play()
      .then(() => {
        this.unlocked = true;
        this.pendingKey = null;
      })
      .catch(() => {
        // Background tab / autoplay policy — retry when the user focuses the app.
        this.pendingKey = key;
      });
  }

  /** Stop a single clip if it is currently playing. */
  stop(key: string): void {
    if (!this.isBrowser) return;

    const audio = this.audioMap.get(key);
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    if (this.pendingKey === key) {
      this.pendingKey = null;
    }
  }

  /** Pause every active clip. */
  stopAll(): void {
    if (!this.isBrowser) return;

    this.audioMap.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    this.pendingKey = null;
  }
}
